import { Box, styled, Typography } from "@mui/material";
import { api, types } from "api";
import { AssignmentModelList } from "components/AssignmentModelList/AssignmentModelList";
import { InfoSnackBar } from "components/InfoSnackBar";
import { ComponentProps, Loader } from "components/Loader";
import { StyledButton } from "components/StyledButton";
import { SummaryCard } from "components/SummaryCard";
import { Assignment, getStatus, Status } from "helpers/assignment";
import { useBreadcrumbs } from "helpers/useBreadcrumbs";
import { GROUP_DISPLAY_ERROR } from "messages";
import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { routes } from "routes";
import { StudentList } from "./StudentList";

const Root = styled("div")({
	width: "100%",
	maxHeight: "35vh",
});

const TrackGroup = ({ setLoading }: ComponentProps): JSX.Element => {
	const { updateLastBreadcrumbs } = useBreadcrumbs();
	const [group, setGroup] = useState<types.Group>();
	const [err, setErr] = useState(false);
	const { groupId } = useParams<{ groupId: string }>();
	const [assignments, setAssignments] = useState<Assignment[]>([]);

	const loadGroupAndAssignments = useCallback((): void => {
		api
			.getGroup(groupId)
			.then((res) => {
				if (res.status !== 200) {
					throw new Error();
				} else {
					setGroup(res.data);
					updateLastBreadcrumbs({ name: res.data.name, params: { groupId } });
				}
				return api.getGroupAssignments(groupId);
			})
			.then((res) => {
				if (res.status !== 200) {
					throw new Error();
				}
				const now = new Date();
				setAssignments(
					(res.data || []).map((ass) => ({
						...ass,
						status: getStatus(ass, now),
					})),
				);
				setLoading(false);
			})
			.catch(() => {
				setErr(true);
				setLoading(false);
			});
	}, [groupId, updateLastBreadcrumbs, setLoading]);

	useEffect(() => {
		loadGroupAndAssignments();
	}, [groupId, loadGroupAndAssignments]);

	const stateCount = (state: Status): number => {
		let count = 0;
		assignments.forEach((el) => {
			if (el.status === state) {
				count += 1;
			}
		});
		return count;
	};

	return (
		<Root>
			<Box display="flex" height="max-content">
				<Box width={4 / 5} marginRight={2}>
					<StudentList group={group} />
				</Box>
				<Box
					width={1 / 5}
					display="flex"
					flexDirection="column"
					marginRight={2}
				>
					<SummaryCard count={stateCount("In Progress")}>
						ON-GOING ASSIGNMENTS
					</SummaryCard>
					<SummaryCard count={stateCount("Scheduled")}>
						SCHEDULED ASSIGNMENTS
					</SummaryCard>
					<SummaryCard count={stateCount("Completed")}>
						COMPLETED ASSIGNMENTS
					</SummaryCard>
				</Box>
			</Box>
			<Box marginTop={1}>
				{assignments.length === 0 ? (
					<Typography variant="h5">No ongoing assignments</Typography>
				) : (
					<>
						<Box display="flex" justifyContent="space-between" marginBottom={2}>
							<Typography variant="h4">Assignments</Typography>
							<StyledButton
								variant="contained"
								color="primary"
								component={Link}
								to={routes._.assignments._.create.path}
								data-testid="create-button"
							>
								CREATE
							</StyledButton>
						</Box>
						<AssignmentModelList assignments={assignments} show_csv={false} />
					</>
				)}
			</Box>
			<InfoSnackBar
				message={GROUP_DISPLAY_ERROR}
				onClose={() => setErr(false)}
				isOpen={err}
				severity="error"
			/>
		</Root>
	);
};

export const TrackGroupPage = Loader(TrackGroup, "We are fetching your group");
